import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext.jsx";
import { api } from "../api/client.js";
import { useLoading } from "../loading/LoadingContext.jsx";
import DateRangePicker from "../components/DateRangePicker.jsx";

function toIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

// Default to the last 3 months to today — invoices are monthly, so "this
// month to date" (the default elsewhere) would usually show nothing yet.
function defaultInvoiceRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  return { startDate: toIsoDate(start), endDate: toIsoDate(now) };
}

function formatMoney(amount, currency) {
  if (amount == null) return "—";
  const n = Number(amount);
  return `${currency || ""} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

const INVOICE_TYPE_BADGE = { INVOICE: "badge-master", CREDIT_MEMO: "badge-member", PAYMENT_RECEIPT: "badge-standalone" };

function InvoiceTypeBadge({ type }) {
  if (!type) return <span className="muted">—</span>;
  return <span className={`badge ${INVOICE_TYPE_BADGE[type] || "badge-standalone"}`}>{type.replace(/_/g, " ")}</span>;
}

function DownloadButton({ idToken, accountId, invoiceId }) {
  const { start, stop } = useLoading();
  const [state, setState] = useState("idle"); // idle | loading | error
  const [errorMsg, setErrorMsg] = useState(null);

  const onDownload = async () => {
    setState("loading");
    setErrorMsg(null);
    start();
    try {
      const pdf = await api.getInvoicePdf(idToken, accountId, invoiceId);
      if (!pdf.document_url) throw new Error("No PDF URL returned");
      window.open(pdf.document_url, "_blank", "noopener,noreferrer");
      setState("idle");
    } catch (err) {
      setErrorMsg(err.message);
      setState("error");
    } finally {
      stop();
    }
  };

  return (
    <div style={{ textAlign: "right" }}>
      <button className="link" type="button" onClick={onDownload} disabled={state === "loading"}>
        {state === "loading" ? "Preparing…" : "Download PDF"}
      </button>
      {state === "error" && (
        <div className="error-text" style={{ fontSize: 11, marginTop: 2 }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
}

export default function Invoices() {
  const { idToken } = useAuth();
  const { start, stop } = useLoading();
  const [range, setRange] = useState(defaultInvoiceRange());
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [consolidatedFrom, setConsolidatedFrom] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [meta, setMeta] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  // AWS's Invoicing API has no payment/settlement status field at all (confirmed
  // against the actual service model — InvoiceSummary exposes InvoiceType,
  // BillType, TaxAuthorityStatus, etc., but nothing like "paid"/"unpaid"). The
  // closest available proxy is InvoiceType: hide PAYMENT_RECEIPT (AWS already
  // has your money) and CREDIT_MEMO (a correction, not something owed) and show
  // only INVOICE rows by default. This is NOT a real payment-status check — an
  // INVOICE row can already be paid — so it's off by a toggle rather than baked
  // in silently.
  const [showAllTypes, setShowAllTypes] = useState(false);

  useEffect(() => {
    start();
    api
      .getAccounts(idToken)
      .then((r) => {
        // Member accounts have no invoices of their own — AWS invoices the
        // payer, not each Organizations member (see consolidatedFrom handling
        // below). Picking one here would just show the payer's invoices again
        // under the member's name, so leave them out of the picker entirely
        // rather than let people select a redundant option.
        const payable = r.accounts.filter((a) => a.org_role !== "member");
        setAccountOptions(payable);
        if (payable.length && !accountId) setAccountId(payable[0].account_id);
      })
      .catch((err) => setErrorMsg(err.message))
      .finally(stop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]);

  const load = (force) => {
    if (!accountId) return;
    setErrorMsg(null);
    start();
    api
      .listInvoices(idToken, accountId, range.startDate, range.endDate, force)
      .then((r) => {
        setInvoices(r.invoices);
        setConsolidatedFrom(r.consolidated_from || null);
        setTruncated(!!r.truncated);
        setMeta({ cached: r.cached, cachedAt: r.cached_at });
      })
      .catch((err) => setErrorMsg(err.message))
      .finally(stop);
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, accountId, range.startDate, range.endDate]);

  const selectedAccountName = accountOptions.find((a) => a.account_id === accountId)?.account_name;
  const parentAccountName = accountOptions.find((a) => a.account_id === consolidatedFrom)?.account_name;

  const visibleInvoices = showAllTypes ? invoices : invoices.filter((inv) => inv.invoice_type === "INVOICE");
  const hiddenCount = invoices.length - visibleInvoices.length;

  return (
    <div>
      <div className="topbar">
        <h2>Invoices</h2>
        <div className="topbar-controls">
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accountOptions.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_name}
              </option>
            ))}
          </select>
          <DateRangePicker value={range} onChange={setRange} />
          <label className="checkbox">
            <input type="checkbox" checked={showAllTypes} onChange={(e) => setShowAllTypes(e.target.checked)} />
            Show receipts &amp; credit memos
          </label>
        </div>
      </div>
      {errorMsg && <div className="error-banner">{errorMsg}</div>}
      <p className="page-hint">
        AWS invoices for {selectedAccountName || "the selected account"}, pulled from AWS's Invoicing API. AWS only has
        invoice data from 2025-06-01 onward — a range entirely before that will show nothing. Click{" "}
        <strong>Download PDF</strong> to fetch a fresh, short-lived link straight from AWS (each click requests a new
        one — links aren't stored or re-used).{" "}
        {!showAllTypes && (
          <>
            Showing only <strong>INVOICE</strong>-type records{hiddenCount > 0 ? ` (${hiddenCount} payment receipt${hiddenCount === 1 ? "" : "s"}/credit memo${hiddenCount === 1 ? "" : "s"} hidden)` : ""} — check{" "}
            <strong>Show receipts &amp; credit memos</strong> to see everything. Note this isn't a true paid/unpaid
            filter: AWS's Invoicing API doesn't report payment or settlement status at all, so an INVOICE row here can
            already be paid.
          </>
        )}
        {consolidatedFrom && (
          <>
            {" "}
            <strong>{selectedAccountName}</strong> is an AWS Organizations member — AWS doesn't invoice member accounts
            separately, so these are the consolidated invoices issued to its payer, <strong>{parentAccountName || consolidatedFrom}</strong>.
          </>
        )}
        {truncated && <> Showing the first page of results only — narrow the date range to see the rest.</>}
        {meta && (
          <>
            {" "}
            {meta.cached ? "Cached" : "Fetched live"} as of {meta.cachedAt ? new Date(meta.cachedAt).toLocaleString() : "—"} —{" "}
            <button type="button" className="link" onClick={() => load(true)}>
              refresh from AWS
            </button>
          </>
        )}
      </p>
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Invoice ID</th>
              <th>Billing period</th>
              <th>Type</th>
              <th>Issued</th>
              <th>Due</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleInvoices.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-row">
                  {invoices.length === 0
                    ? "No invoices for this account/date range."
                    : "No INVOICE-type records for this account/date range — check “Show receipts & credit memos” above to see what's there."}
                </td>
              </tr>
            )}
            {visibleInvoices.map((inv) => (
              <tr key={inv.invoice_id}>
                <td>
                  <span className="mono">{inv.invoice_id}</span>
                  {/* AWS can legitimately issue more than one invoice record for the same
                      billing period (e.g. a standard invoice alongside a local/regional
                      tax-compliant one) — surfacing bill type/receiver/frequency here is
                      what actually tells two same-period rows apart, instead of guessing. */}
                  {(inv.bill_type || inv.receiver_role || inv.invoice_frequency) && (
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {[inv.bill_type, inv.receiver_role, inv.invoice_frequency].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </td>
                <td>
                  {inv.billing_period?.year && inv.billing_period?.month
                    ? `${inv.billing_period.year}-${String(inv.billing_period.month).padStart(2, "0")}`
                    : "—"}
                </td>
                <td>
                  <InvoiceTypeBadge type={inv.invoice_type} />
                </td>
                <td className="muted">{formatDate(inv.issued_date)}</td>
                <td className="muted">{formatDate(inv.due_date)}</td>
                <td>{formatMoney(inv.total_amount, inv.currency_code)}</td>
                <td>
                  {/* Pass the originally selected account_id, not consolidatedFrom — the
                      backend re-resolves the payer itself (same logic that produced
                      consolidatedFrom here), so there's one source of truth for that
                      resolution rather than duplicating it client-side. */}
                  <DownloadButton idToken={idToken} accountId={accountId} invoiceId={inv.invoice_id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
